import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { uploadFile, baseImageToOss, generateRandomCode } from '../../utils/common';
import request from '../../utils/request';
import { loadImagesFromAPI, ImageItem } from '../../utils/imageUploadUtils';
import { 
  PhotoIcon,
  TrashIcon,
  ShareIcon,
  EyeIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import {OSS_BUCKET, OSS_BASE_URL} from '../../config';


const MyImages: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useState(getCurrentUser());
  const [images, setImages] = useState<ImageItem[]>([]);
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 加载图片数据
  useEffect(() => {
    const loadImages = async () => {
      setIsLoading(true);
      try {
        const result = await loadImagesFromAPI(1, '', 'all', 50);
        setImages(result.images);
      } catch (error) {
        console.error('Failed to load images:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadImages();
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const imageTypes = ['all', ...Array.from(new Set(images.map(img => img.type)))];
  
  const filteredImages = selectedType === 'all' 
    ? images 
    : images.filter(image => image.type === selectedType);

  const handleDeleteImage = async (imageId: number) => {
    try {
      const response = await request({
        url: 'images/delete',
        method: 'DELETE',
        data: { id: imageId }
      });
      
      if (response.success) {
        // 从本地状态中移除图片
        setImages(images.filter(image => image.id !== imageId));
      } else {
        console.warn('Failed to delete image:', response.message);
        alert('Failed to delete image. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  const handleUseInDesign = (imageUrl: string) => {
    navigate(`/products?image=${encodeURIComponent(imageUrl)}`);
  };

  const handleDownloadImage = (imageUrl: string, imageName: string) => {
    // 创建一个临时的 a 标签来下载图片
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageName;
    link.target = '_blank';
    
    // 添加到 DOM 中并触发点击
    document.body.appendChild(link);
    link.click();
    
    // 清理 DOM
    document.body.removeChild(link);
  };

  const handleTogglePublic = async (imageId: number) => {
    try {
      // 找到当前图片
      const currentImage = images.find(image => image.id === imageId);
      if (!currentImage) return;
      
      // 准备新的公开状态
      const newPublicStatus = !currentImage.isPublic;
      
      const response = await request({
        url: 'images/public',
        method: 'POST',
        data: { 
          id: imageId,
          public: newPublicStatus ? 1 : 0 
        }
      });
      
      if (response.success) {
        console.log('Image public status updated successfully');
        // 更新本地状态
        setImages(images.map(image => 
          image.id === imageId 
            ? { ...image, isPublic: newPublicStatus }
            : image
        ));
      } else {
        console.warn('Failed to update image public status:', response.message);
        alert('Failed to update image public status. Please try again.');
      }
    } catch (error) {
      console.error('Error updating image public status:', error);
      alert('Error updating image public status. Please try again.');
    }
  };

  // 获取文件扩展名的辅助函数
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg'; // 默认扩展名
    }
    return filename.substring(lastDotIndex);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (files && files.length > 0) {
      setIsUploading(true);
      try {
        const newImages: ImageItem[] = [];
        let maxId = Math.max(...images.map(img => img.id));
        
        // 处理多个文件上传，使用Promise.all等待所有文件处理完成
        const filePromises = Array.from(files).map(async (file) => {
          // 检查是否为图片文件
          if (!file.type.startsWith('image/')) {
            console.warn(`Skipping non-image file: ${file.name}`);
            return null;
          }
          
          try {
            // 将文件转换为base64
            const base64String = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                resolve(event.target?.result as string);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            
            try {
              // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
              const uploadedName = await baseImageToOss(
                generateRandomCode() + getFileExtension(file.name), 
                base64String, 
                OSS_BUCKET
              );
              
              console.log('上传成功:', uploadedName);
              
              const newImage: ImageItem = {
                id: ++maxId,
                name: file.name,
                size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
                uploadDate: new Date().toISOString().split('T')[0],
                type: 'Uploaded',
                isPublic: false,
                views: 0,
                downloads: 0,
                url: `${OSS_BASE_URL}${uploadedName}` // 使用OSS URL
              };
              return newImage;
              
            } catch (uploadError) {
              console.error('上传到OSS失败:', uploadError);
              // 如果上传失败，使用本地URL作为备选
              const imageUrl = URL.createObjectURL(file);
              const newImage: ImageItem = {
                id: ++maxId,
                name: file.name,
                size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
                uploadDate: new Date().toISOString().split('T')[0],
                type: 'Uploaded',
                isPublic: false,
                views: 0,
                downloads: 0,
                url: imageUrl
              };
              return newImage;
            }
            
          } catch (error) {
            console.error(`文件处理失败 ${file.name}:`, error);
            return null;
          }
        });
        
        // 等待所有文件处理完成
        const results = await Promise.all(filePromises);
        newImages.push(...results.filter((result): result is ImageItem => result !== null));
        
        console.log('newImages', newImages);
        // 提交新图片数据到 /images/add 接口
        if (newImages.length > 0) {
          try {
            const response = await request({
              url: 'images/add',
              method: 'POST',
              data: { images: newImages }
            });
            
            if (response.success) {
              console.log('Images added to database successfully');
              // 重新加载图片列表以显示最新数据
              const result = await loadImagesFromAPI(1, '', 'all', 50);
              setImages(result.images);
            } else {
              console.warn('Failed to add images to database:', response.message);
              // 即使数据库提交失败，也更新本地状态
              setImages([...newImages, ...images]);
            }
          } catch (error) {
            console.error('Error adding images to database:', error);
            // 即使API调用失败，也更新本地状态
            setImages([...newImages, ...images]);
          }
        }
        
        // 显示上传结果
        if (newImages.length > 0) {
          const successCount = newImages.length;
          const totalCount = files.length;
          // if (successCount === totalCount) {
          //   alert(`Successfully uploaded ${successCount} image(s)!`);
          // } else {
          //   alert(`Uploaded ${successCount} out of ${totalCount} files. Some files failed to upload.`);
          // }
        }
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Upload failed. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(event.target.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      // 过滤只接受图片文件
      const imageFiles = Array.from(files).filter(file => 
        file.type.startsWith('image/')
      );
      
      if (imageFiles.length > 0) {
        // 创建 FileList 对象，支持多文件
        const dataTransfer = new DataTransfer();
        imageFiles.forEach(file => {
          dataTransfer.items.add(file);
        });
        handleFileUpload(dataTransfer.files);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Images</h1>
          <p className="text-gray-600">Manage your uploaded images and photos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <AccountSidebar currentPage="my-images" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Upload Images</h2>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  >
                    {imageTypes.map(type => (
                      <option key={type} value={type}>
                        {type === 'all' ? 'All Types' : type}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center space-x-2">
                    <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                    >
                      <span className="text-lg">⊞</span>
                    </button>
                    <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                    >
                      <span className="text-lg">☰</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragOver 
                    ? 'border-gray-300 hover:border-gray-400'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={isDragOver ? { borderColor: '#68aeb0', backgroundColor: '#f0f2f5' } : {}}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="hidden"
                  id="image-upload"
                  multiple
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-4 ${
                    isDragOver ? '' : 'text-gray-400'
                  }`} />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {isUploading ? 'Uploading...' : 'Upload Images'}
                  </p>
                  <p className="text-gray-500 mb-4">
                    Drag and drop images here, or click to select files
                  </p>
                  <p className="text-sm text-gray-400">
                    Supports JPG, PNG, GIF up to 10MB each • Multiple files supported
                  </p>
                </label>
              </div>
            </div>

            {/* Images Grid/List */}
            {isLoading ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading images...</h3>
                <p className="text-gray-600">Please wait while we fetch your images.</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredImages.map((image) => (
                  <div key={image.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
                      {image.url ? (
                        <img 
                          src={image.url} 
                          alt={image.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // 图片加载失败时显示占位图标
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <PhotoIcon className={`w-12 h-12 text-gray-400 ${image.url ? 'hidden' : ''}`} />
                      <div className="absolute top-2 right-2 flex space-x-1">
                        {/* <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                          <ShareIcon className="w-4 h-4 text-gray-600" />
                        </button> */}
                        <button 
                          onClick={() => handleDeleteImage(image.id)}
                          className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">
                          {image.name}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">{image.type}</span>  
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {image.size} • Uploaded {new Date(image.uploadDate).toLocaleDateString()}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span className="flex items-center">
                            <EyeIcon className="w-3 h-3 mr-1" />
                            {image.views}
                          </span>
                          <span className="flex items-center">
                            <span className="w-3 h-3 mr-1">⬇️</span>
                            {image.downloads}
                          </span>
                        </div>
                        <button
                          onClick={() => handleTogglePublic(image.id)}
                          className={`px-2 py-1 text-xs rounded-full ${
                            image.isPublic 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {image.isPublic ? 'Public' : 'Private'}
                        </button>
                      </div>
                      <div className="mt-3 space-y-2">
                        <button 
                          onClick={() => handleUseInDesign(image.url || '')}
                          className="w-full text-white py-2 px-3 rounded-lg transition-colors text-sm"
                          style={{ backgroundColor: '#2e3a47' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                        >
                          Use in Design
                        </button>
                        <button 
                          onClick={() => handleDownloadImage(image.url || '', image.name)}
                          className="w-full border border-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredImages.map((image) => (
                  <div key={image.id} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {image.url ? (
                          <img 
                            src={image.url} 
                            alt={image.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // 图片加载失败时显示占位图标
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <PhotoIcon className={`w-8 h-8 text-gray-400 ${image.url ? 'hidden' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{image.name}</h3>
                            <p className="text-sm text-gray-500 mb-2">
                              {image.type} • {image.size} • Uploaded {new Date(image.uploadDate).toLocaleDateString()}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="flex items-center">
                                <EyeIcon className="w-4 h-4 mr-1" />
                                {image.views} views
                              </span>
                              <span className="flex items-center">
                                <span className="w-4 h-4 mr-1">⬇️</span>
                                {image.downloads} downloads
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleTogglePublic(image.id)}
                              className={`px-3 py-1 text-sm rounded-full ${
                                image.isPublic 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {image.isPublic ? 'Public' : 'Private'}
                             </button>
                            {/*<button className="p-2 text-gray-400 hover:text-gray-600">
                              <ShareIcon className="w-4 h-4" />
                            </button> */}
                            <button 
                              onClick={() => handleDeleteImage(image.id)}
                              className="p-2 text-red-400 hover:text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-3">
                          <button 
                            onClick={() => handleUseInDesign(image.url || '')}
                            className="text-white py-2 px-4 rounded-lg transition-colors text-sm"
                            style={{ backgroundColor: '#2e3a47' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                          >
                            Use in Design
                          </button>
                          <button 
                            onClick={() => handleDownloadImage(image.url || '', image.name)}
                            className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {filteredImages.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No images found</h3>
                <p className="text-gray-600 mb-6">
                  {selectedType === 'all' 
                    ? "You haven't uploaded any images yet." 
                    : `No images in ${selectedType} category.`}
                </p>
                <label htmlFor="image-upload" className="inline-flex items-center px-6 py-3 text-white rounded-lg cursor-pointer transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}>
                  <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                  Upload Your First Image
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyImages; 